import React from 'react'

import { WorkspaceLogo, AvatarStack, useModalContext } from '@sketch/components'
import { useFlag } from '@sketch/modules-common'
import ProjectMembershipAvatar from '../ProjectMembershipAvatar'
import { ProjectSharingModal } from '../../modals/ProjectSharingModal'
import { NewProjectSharingModal } from '../../modals/NewProjectSharingModal'
import {
  SharingButton as StyledButton,
  TextWrapper,
} from './ProjectHeader.styles'
import { ProjectWorkspaceAccessLevel } from 'modules/projects/types'
import {
  WorkspaceMinimalFragment,
  ProjectWithPrivacyIconFragment,
  GetProjectMembershipsQuery,
  ProjectMembershipFragment,
} from '@sketch/gql-types'
import { ProjectWithShares } from 'modules/projects/modals/NewProjectSharingModal/types'

type SharingButtonProps = {
  project: ProjectWithPrivacyIconFragment
  workspace: WorkspaceMinimalFragment
  workspaceAccessLevel: ProjectWorkspaceAccessLevel
  userCanRenameAndDeleteProject?: boolean
  memberships?: GetProjectMembershipsQuery['project']['memberships']
}

type PrivacyAndWorkspaceIconsProps = {
  project: ProjectWithPrivacyIconFragment
  workspace: WorkspaceMinimalFragment
}

const PrivacyAndWorkspaceIcons: React.FC<PrivacyAndWorkspaceIconsProps> = props => {
  const { project, workspace } = props

  if (project.privacyIcon === 'INVITED_WORKSPACE_MEMBERS') {
    // For "restricted" projects, we show the lock icon without the workspace avatar/
    // Users must be invited to access shares in this project.
    return (
      <WorkspaceLogo.Icon
        size="24px"
        icon="lock"
        data-testid="workspace-logo-lock"
      />
    )
  } else if (project.privacyIcon === 'GUESTS') {
    // For non-restricted projects with guests invited, we show the "people"
    // icon to represent guests along with the workspace avatar.
    return (
      <>
        <WorkspaceLogo.Icon
          size="24px"
          icon="people"
          data-testid="workspace-logo-people"
        />
        <WorkspaceLogo
          key={workspace.identifier}
          size="24px"
          workspaceName={workspace.name}
          src={workspace.avatar?.small}
        />
      </>
    )
  } else {
    // For non-restricted projects with no guests invited, we show the workspace avatar
    return (
      <WorkspaceLogo
        key={workspace.identifier}
        size="24px"
        workspaceName={workspace.name}
        src={workspace.avatar?.small}
      />
    )
  }
}

const SharingButton = ({
  project,
  memberships,
  workspace,
  userCanRenameAndDeleteProject,
}: SharingButtonProps) => {
  const { showModal } = useModalContext()
  const isNewSharingFFEnabled = useFlag('new-sharing')

  const totalMembershipsCount = memberships?.meta?.totalCount || 0

  const members = memberships?.entries || []

  return (
    <StyledButton
      data-testid="project-sharing-settings"
      onClick={() => {
        isNewSharingFFEnabled
          ? showModal(NewProjectSharingModal, {
              project: project as ProjectWithShares,
              workspace,
            })
          : showModal(ProjectSharingModal, {
              project,
              workspace,
              workspaceId: workspace.identifier,
              editable: userCanRenameAndDeleteProject && !project.deletedAt,
            })
      }}
    >
      <AvatarStack size="24px" totalCount={totalMembershipsCount}>
        <PrivacyAndWorkspaceIcons workspace={workspace} project={project} />
        {members.map((member: ProjectMembershipFragment) => (
          <ProjectMembershipAvatar
            key={member.identifier}
            membership={member}
            size="24px"
          />
        ))}
      </AvatarStack>

      <TextWrapper>
        {userCanRenameAndDeleteProject ? 'Share…' : 'Sharing'}
      </TextWrapper>
    </StyledButton>
  )
}

export default SharingButton
